import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { IMediaBankFile } from '@fi-shared/media-bank';
import { FileType, StatusCodeType } from '@fi-shared/types';
import {
    HttpClient,
    HttpErrorResponse,
    HttpEventType,
    HttpHeaders,
    HttpParams,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import { REGEX_MATCH_VIMEO } from '@fi-shared/regexes';
import { FiVimeoCreateVideo, FiVimeoInfo } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo';
import { toFiPromise } from '@fi-shared/to-fi-promise';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiMediaBankDownloadData } from '@fi-shared/media-bank/media-bank';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { first } from 'rxjs/operators';
import { VimeoVideoTranscodeStatus } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-enum';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';

export interface IVimeoUploadOptions {
    onProgress?: (event: { loaded: number; total?: number }) => void;
    onAbort?: Subject<void>;
}

export interface IVimeoUploadResult {
    vimeoId: string;
    width: number;
    height: number;
    createdTime: string;
}

export interface IVimeoOverlayDialogData {
    safeUrl: SafeResourceUrl;
    videoId: string;
    videoHeight: number;
    videoWidth: number;
}

export const VIMEO_PLAYER_URL = 'https://player.vimeo.com/video';

@Injectable()
export class FiVimeoService {
    static DEFAULT_VIMEO_EXTENSION: string = '.mp4';
    private renderer: Renderer2;

    constructor(
        private http: HttpClient,
        private customerDataService: FiCustomerDataService,
        private sanitizer: DomSanitizer,
        private fiLoading: LoadingService,
        rendererFactory: RendererFactory2,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);

        setTimeout(() => {
            //const vimeoToken = this.customerDataService.getVimeoToken();
            /*const verifyRequest = this.http.get('https://api.vimeo.com/oauth/verify', {
                headers: new HttpHeaders()
                    .set('Authorization', `bearer ${this.vimeoConfig.token}`)
                    .set('Content-Type', `application/json`)
                    .set('Accept', `application/vnd.vimeo.*+json;version=3.4`),
            });
            verifyRequest.subscribe(result => {
            })*/
            /*
            const authorizationRequest = this.http.post('https://api.vimeo.com/oauth/authorize/client', {
                "grant_type": "client_credentials",
                "scope": "private create edit delete upload video_files public"
            }, {
                headers: new HttpHeaders()
                    .set('Authorization', `basic ${auth}`)
                    .set('Content-Type', `application/json`)
                    .set('Accept', `application/vnd.vimeo.*+json;version=3.4`),
            });
            const promise = toFiPromise(authorizationRequest);
            promise.then((authorizationResult: any) => {
            })
*/
        }, 2000);
    }

    uploadToVimeo(fileToUpload: File, uploadOptions: IVimeoUploadOptions) {
        return new Promise<IVimeoUploadResult>((resolve, reject: (err: HttpErrorResponse) => void) => {
            this.customerDataService.vimeoToken$.pipe(first()).subscribe((vimeoToken) => {
                const uploadRequestBody: any = {
                    upload: {
                        approach: 'tus',
                        size: fileToUpload.size,
                    },
                    name: fileToUpload.name.substr(0, fileToUpload.name.lastIndexOf('.')) || fileToUpload.name,
                };
                const allowedVimeoDomains = this.customerDataService.getCustomerData().AllowedVimeoDomains;
                if (allowedVimeoDomains) {
                    uploadRequestBody.privacy = {
                        embed: 'whitelist',
                        //allowed_domains: allowedVimeoDomains,
                    };
                }
                const request = this.http.post('https://api.vimeo.com/me/videos', uploadRequestBody, {
                    headers: this.getHeadersForVimeoRequest(vimeoToken),
                });
                const promise = toFiPromise<FiVimeoCreateVideo>(request);
                promise.then((data: FiVimeoCreateVideo) => {
                    const headers = new HttpHeaders({
                        'Tus-Resumable': '1.0.0',
                        'Upload-Offset': '0',
                        'Content-Type': 'application/offset+octet-stream',
                    });
                    const params = new HttpParams();
                    const options = {
                        params: params,
                        reportProgress: true,
                        headers: headers,
                    };
                    let abortSubscription: Subscription;
                    const req = new HttpRequest('PATCH', data.upload.upload_link, fileToUpload, options);
                    const request = this.http.request(req).subscribe(
                        (event) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                if (uploadOptions.onProgress) {
                                    uploadOptions.onProgress(event);
                                }
                            } else if (event instanceof HttpResponse) {
                                // Close the progress-stream if we get an answer form the API
                                // The upload is complete
                                if (abortSubscription) {
                                    abortSubscription.unsubscribe();
                                }

                                const VIMEOmatches = REGEX_MATCH_VIMEO.exec(data.link);
                                if (VIMEOmatches && VIMEOmatches[5]) {
                                    const vimeoId = VIMEOmatches[5];

                                    this.setTagsToVimeo(vimeoId);
                                    if (allowedVimeoDomains) {
                                        this.setAllowedDomains(vimeoId, allowedVimeoDomains);
                                    }

                                    resolve({
                                        vimeoId: vimeoId,
                                        width: data.width,
                                        height: data.height,
                                        createdTime: data.created_time,
                                    });
                                } else {
                                    reject(
                                        new HttpErrorResponse({
                                            status: StatusCodeType.BadRequest,
                                        }),
                                    );
                                }
                            }
                        },
                        (err: HttpErrorResponse) => {
                            if (abortSubscription) {
                                abortSubscription.unsubscribe();
                            }
                            reject(err);
                        },
                    );

                    if (uploadOptions.onAbort) {
                        abortSubscription = uploadOptions.onAbort.subscribe(() => {
                            request.unsubscribe();
                        });
                    }
                }, reject);
            });
        });
    }

    shouldUploadToVimeo(inProcessFile: IMediaBankFile) {
        return (
            (inProcessFile.FileType === FileType.Movie || inProcessFile.FileType === FileType.MovieNotPlayable) &&
            this.customerDataService.customerData.VimeoEnabled
        );
    }

    getDownloadInfo(vimeoId: string): Promise<FiMediaBankDownloadData[]> {
        const promise = new Promise<FiMediaBankDownloadData[]>((resolve, reject) => {
            this.customerDataService.vimeoToken$.pipe(first()).subscribe((vimeoToken) => {
                const request = this.http.get(`https://api.vimeo.com/me/videos/${vimeoId}?fields=download`, {
                    headers: this.getHeadersForVimeoRequest(vimeoToken),
                });
                const promise = toFiPromise(request);
                promise.then((data: any) => {
                    if (data && data.download && data.download.length) {
                        resolve(data.download);
                    } else {
                        reject('no vimeo download data found ' + JSON.stringify(data));
                    }
                }, reject);
            });
        });
        return this.fiLoading.trackPromise(promise);
    }

    private getHeadersForVimeoRequest(vimeoToken: string) {
        return new HttpHeaders()
            .set('Authorization', `bearer ${vimeoToken}`)
            .set('Content-Type', `application/json`)
            .set('Accept', `application/vnd.vimeo.*+json;version=3.4`);
    }

    getVideoInfo(vimeoId: string) {
        return new Promise<FiVimeoInfo>((resolve, reject) => {
            this.customerDataService.vimeoToken$.pipe(first()).subscribe((vimeoToken) => {
                const request = this.http.get(
                    `https://api.vimeo.com/me/videos/${vimeoId}?fields=transcode.status,pictures.sizes,width,height`,
                    {
                        headers: this.getHeadersForVimeoRequest(vimeoToken),
                    },
                );
                const promise = toFiPromise(request);
                promise.then((data: any) => {
                    const result: FiVimeoInfo = {
                        isAvailable:
                            data && data.transcode && data.transcode.status === VimeoVideoTranscodeStatus.Complete,
                        width: data.width,
                        height: data.height,
                        status: data.transcode.status,
                    };
                    if (data.pictures && data.pictures.sizes) {
                        result.previewPictures = data.pictures.sizes.map((item) => ({
                            width: item.width,
                            height: item.height,
                            link: item.link,
                        }));
                    }
                    resolve(result);
                }, reject);
            });
        });
    }

    getVimeoSafeUrl(videoId: string, autoplayMode: boolean = true, playerId?: string): SafeUrl {
        let url = `${VIMEO_PLAYER_URL}/${videoId}`;
        const params: string[] = [];

        if (playerId) {
            // Add api=1 to enable Vimeo Player API
            params.push('api=1');
            params.push(`player_id=${playerId}`);
        }

        params.push(`autoplay=${autoplayMode ? 1 : 0}`);

        url += `?${params.join('&')}`;
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    setVimeoPreviewIconsSize(element: HTMLElement) {
        const vimeoPreviewImages: NodeList = element.querySelectorAll('[fi-vimeo-preview-image]');
        vimeoPreviewImages.forEach((node: Node) => {
            const boundingClientRect = (node as HTMLElement).getBoundingClientRect();
            if (boundingClientRect.width > 0 && boundingClientRect.height > 0) {
                this.renderer.setStyle(
                    node.parentNode as HTMLElement,
                    'font-size',
                    `${Math.min(boundingClientRect.width, boundingClientRect.height) / 2}px`,
                );
            }
        });
    }

    private setTagsToVimeo(vimeoId: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.customerDataService.vimeoToken$.pipe(first()).subscribe((vimeoToken) => {
                /* // for tests
                const request2 = this.http.get(`https://api.vimeo.com/videos/${vimeoId}`, {
                    headers: this.getHeadersForVimeoRequest(vimeoToken),
                });
                const promise2 = toFiPromise(request2);
                promise2.then(res => {
                })*/

                const request = this.http.put(
                    `https://api.vimeo.com/videos/${vimeoId}/tags`,
                    [
                        { name: 'customerId@' + this.customerDataService.customerData.CustomerId },
                        { name: 'customerName@' + this.customerDataService.customerData.CustomerName },
                    ],
                    {
                        headers: this.getHeadersForVimeoRequest(vimeoToken),
                    },
                );
                const promise = toFiPromise(request);
                promise.then(resolve, reject);
            });
        });
    }

    private setAllowedDomains(vimeoId: string, allowedDomains: string[]): Promise<any> {
        const promises = allowedDomains.map((allowedDomain) => {
            return new Promise<void>((resolve, reject) => {
                this.customerDataService.vimeoToken$.pipe(first()).subscribe((vimeoToken) => {
                    const request = this.http.put(
                        `https://api.vimeo.com/videos/${vimeoId}/privacy/domains/${encodeURIComponent(allowedDomain)}`,
                        {},
                        {
                            headers: this.getHeadersForVimeoRequest(vimeoToken),
                        },
                    );
                    const promise = toFiPromise(request);
                    promise.then(resolve, reject);
                });
            });
        });

        return Promise.all(promises);
    }
}
